import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { complianceApi, driverApplicationsApi } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Users,
  ShieldCheck,
  ShieldAlert,
  CalendarClock,
  CreditCard,
  HeartPulse,
  FileText,
  AlertTriangle,
  Clock,
  ChevronRight,
  Upload,
  Send,
  Download,
  UserPlus,
  ClipboardList,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CircleDot,
  Copy,
  Mail,
  MessageSquare,
  Share2,
  MapPin,
  Map,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const pickupIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33],
});
const deliveryIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33],
});
const driverIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33],
});

const SEVERITY_ICONS = {
  critical: XCircle,
  high: AlertTriangle,
  medium: AlertCircle,
  low: CircleDot,
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APPLY_URL = `${window.location.origin}/apply`;

function KpiCard({ label, value, icon: Icon, color = 'blue', loading, linkTo }) {
  const colorMap = {
    blue: 'text-blue-600 bg-blue-500/10 dark:text-blue-400 dark:bg-blue-500/20',
    emerald: 'text-emerald-600 bg-emerald-500/10 dark:text-emerald-400 dark:bg-emerald-500/20',
    rose: 'text-rose-600 bg-rose-500/10 dark:text-rose-400 dark:bg-rose-500/20',
    amber: 'text-amber-600 bg-amber-500/10 dark:text-amber-400 dark:bg-amber-500/20',
    purple: 'text-purple-600 bg-purple-500/10 dark:text-purple-400 dark:bg-purple-500/20',
    sky: 'text-sky-600 bg-sky-500/10 dark:text-sky-400 dark:bg-sky-500/20',
    slate: 'text-slate-600 bg-slate-500/10 dark:text-slate-400 dark:bg-slate-500/20',
  };
  const cls = colorMap[color] || colorMap.blue;
  const content = (
    <div className="bg-card border border-border rounded-md p-4 flex items-start justify-between hover:shadow-sm transition-shadow duration-200 cursor-pointer" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        {loading ? <Skeleton className="h-8 w-16" /> : <p className="font-cabinet text-3xl font-bold text-foreground">{value}</p>}
      </div>
      <div className={`p-2 rounded-md ${cls}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  );
  if (linkTo) return <Link to={linkTo}>{content}</Link>;
  return content;
}

function StatusBadge({ status }) {
  const map = {
    active: { label: 'Active', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    compliant: { label: 'Compliant', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    pending: { label: 'Pending', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400', icon: Clock },
    non_compliant: { label: 'Non-Compliant', cls: 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400', icon: XCircle },
    disqualified: { label: 'Disqualified', cls: 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400', icon: XCircle },
    archived: { label: 'Archived', cls: 'bg-muted border-border text-muted-foreground', icon: CircleDot },
    not_hired: { label: 'Not Hired', cls: 'bg-muted border-border text-muted-foreground', icon: CircleDot },
    new: { label: 'New', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-400', icon: AlertCircle },
    in_review: { label: 'In Review', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400', icon: Clock },
    approved: { label: 'Approved', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400', icon: XCircle },
  };
  const conf = map[status] || map.active;
  const Icon = conf.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium border ${conf.cls}`}>
      <Icon className="h-3 w-3" />{conf.label}
    </span>
  );
}

function ShareDialog({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(APPLY_URL);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('Driver Application - Apply Now');
    const body = encodeURIComponent(`You are invited to complete a driver application.\n\nPlease fill out the form at:\n${APPLY_URL}\n\nThank you.`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleSmsShare = () => {
    const body = encodeURIComponent(`You are invited to complete a driver application. Apply here: ${APPLY_URL}`);
    window.open(`sms:?body=${body}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Driver Application', text: 'Complete your driver application', url: APPLY_URL });
      } catch { /* user cancelled */ }
    } else {
      handleCopy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-600 dark:text-blue-400" /> Invite Applicant</DialogTitle>
          <DialogDescription>Share the application link via email, text message, or copy it directly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Copy Link */}
          <div className="flex items-center gap-2">
            <Input value={APPLY_URL} readOnly className="flex-1 text-sm" data-testid="share-link-input" />
            <Button variant="outline" size="sm" onClick={handleCopy} className="flex-shrink-0" data-testid="copy-link-btn">
              {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          {/* Share Buttons */}
          <div className="grid grid-cols-1 gap-2">
            <Button variant="outline" className="justify-start gap-3 h-11" onClick={handleEmailShare} data-testid="share-email-btn">
              <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" /> Share via Email
            </Button>
            <Button variant="outline" className="justify-start gap-3 h-11" onClick={handleSmsShare} data-testid="share-sms-btn">
              <MessageSquare className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> Share via Text Message
            </Button>
            <Button variant="outline" className="justify-start gap-3 h-11" onClick={handleNativeShare} data-testid="share-native-btn">
              <Share2 className="h-4 w-4 text-purple-600 dark:text-purple-400" /> Share via Other Apps
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FleetMap({ mapData, loading }) {
  if (loading) return <Skeleton className="h-full w-full rounded-md" />;
  if (!mapData || (mapData.loads.length === 0 && mapData.drivers.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <MapPin className="h-10 w-10 text-muted-foreground/30 mb-2" />
        <p className="text-sm text-muted-foreground">No location data available yet. Add loads with pickup/delivery locations to see them on the map.</p>
      </div>
    );
  }

  const allCoords = [];
  mapData.loads.forEach(l => {
    if (l.pickup_coords) allCoords.push(l.pickup_coords);
    if (l.delivery_coords) allCoords.push(l.delivery_coords);
  });
  mapData.drivers.forEach(d => { if (d.coords) allCoords.push(d.coords); });
  const center = allCoords.length > 0 ? [
    allCoords.reduce((s, c) => s + c[0], 0) / allCoords.length,
    allCoords.reduce((s, c) => s + c[1], 0) / allCoords.length,
  ] : [39.8, -98.5];

  return (
    <MapContainer center={center} zoom={4} style={{ height: '100%', width: '100%', borderRadius: '0.375rem' }} scrollWheelZoom={true}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {mapData.drivers.map((d, i) => d.coords && (
        <Marker key={`d-${i}`} position={d.coords} icon={driverIcon}>
          <Popup><strong>{d.name}</strong><br />{d.location}</Popup>
        </Marker>
      ))}
      {mapData.loads.map((l, i) => (
        <React.Fragment key={`l-${i}`}>
          {l.pickup_coords && (
            <Marker position={l.pickup_coords} icon={pickupIcon}>
              <Popup><strong>Pickup</strong><br/>{l.pickup_location}<br/>{l.reference} - {l.broker}</Popup>
            </Marker>
          )}
          {l.delivery_coords && (
            <Marker position={l.delivery_coords} icon={deliveryIcon}>
              <Popup><strong>Delivery</strong><br/>{l.delivery_location}<br/>{l.reference} - {l.broker}</Popup>
            </Marker>
          )}
          {l.pickup_coords && l.delivery_coords && (
            <Polyline positions={[l.pickup_coords, l.delivery_coords]} pathOptions={{ color: '#3b82f6', weight: 2, opacity: 0.5, dashArray: '6 4' }} />
          )}
        </React.Fragment>
      ))}
    </MapContainer>
  );
}

export default function ComplianceDashboard() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    loadData();
    loadMap();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, appsRes] = await Promise.all([
        complianceApi.getDashboardStats(),
        driverApplicationsApi.getAll(),
      ]);
      setStats(statsRes.data);
      setApps(appsRes.data?.slice(0, 5) || []);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadMap = async () => {
    try {
      const res = await complianceApi.getFleetMap();
      setMapData(res.data);
    } catch {
      // non-critical
    } finally {
      setMapLoading(false);
    }
  };

  const complianceDonut = stats ? [
    { name: 'Compliant', value: stats.compliant_drivers, color: '#059669' },
    { name: 'Pending', value: stats.pending_applications, color: '#f59e0b' },
    { name: 'Non-Compliant', value: stats.non_compliant_drivers, color: '#e11d48' },
  ].filter(d => d.value > 0) : [];
  const totalForDonut = complianceDonut.reduce((s, d) => s + d.value, 0);

  return (
    <div className="space-y-6">
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight text-foreground" data-testid="dashboard-title">Compliance Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Driver qualification and compliance overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/drivers">
            <Button variant="outline" size="sm" className="hidden sm:flex" data-testid="quick-add-driver-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add Driver
            </Button>
          </Link>
          <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => setShareOpen(true)} data-testid="invite-applicant-btn">
            <Send className="h-4 w-4 mr-1.5" /> Invite Applicant
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="kpi-cards-grid">
        <KpiCard label="Total Drivers" value={stats?.total_drivers ?? '-'} icon={Users} color="blue" loading={loading} linkTo="/driver-management" />
        <KpiCard label="Compliant" value={stats?.compliant_drivers ?? '-'} icon={ShieldCheck} color="emerald" loading={loading} linkTo="/driver-management?view=all" />
        <KpiCard label="Non-Compliant" value={stats?.non_compliant_drivers ?? '-'} icon={ShieldAlert} color="rose" loading={loading} linkTo="/driver-management?view=non_compliant" />
        <KpiCard label="Reviews Due" value={stats?.reviews_due_soon ?? '-'} icon={CalendarClock} color="amber" loading={loading} />
        <KpiCard label="Expiring CDL" value={stats?.expiring_licenses ?? '-'} icon={CreditCard} color="purple" loading={loading} linkTo="/driver-management?view=expiring_cdl" />
        <KpiCard label="Expiring Medical" value={stats?.expiring_medical_cards ?? '-'} icon={HeartPulse} color="sky" loading={loading} linkTo="/driver-management?view=expiring_medical" />
        <KpiCard label="Pending Apps" value={stats?.pending_applications ?? '-'} icon={FileText} color="slate" loading={loading} linkTo="/driver-files" />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Fleet Map */}
          <div className="bg-card border border-border rounded-md" data-testid="fleet-map-widget">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Map className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Fleet Map</h2>
              </div>
              {mapData && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Pickup</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Delivery</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Drivers</span>
                </div>
              )}
            </div>
            <div className="h-72">
              <FleetMap mapData={mapData} loading={mapLoading} />
            </div>
          </div>

          {/* Action Required */}
          <div className="bg-card border border-border rounded-md" data-testid="action-required-widget">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Action Required</h2>
              </div>
              <Badge variant="outline" className="text-xs">{stats?.action_required?.length || 0}</Badge>
            </div>
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : stats?.action_required?.length > 0 ? (
                stats.action_required.slice(0, 8).map((item, i) => {
                  const SevIcon = SEVERITY_ICONS[item.severity] || AlertCircle;
                  return (
                    <div key={i} className={`flex items-center gap-3 px-4 py-3 ${item.severity === 'critical' ? 'bg-rose-500/5' : 'bg-amber-500/5'}`} style={{ borderLeftWidth: '3px', borderLeftColor: item.severity === 'critical' ? '#e11d48' : '#f59e0b' }}>
                      <SevIcon className={`h-4 w-4 flex-shrink-0 ${item.severity === 'critical' ? 'text-rose-500' : 'text-amber-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.driver_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.detail}</p>
                      </div>
                      <Link to="/driver-management" className="flex-shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">View</Button>
                      </Link>
                    </div>
                  );
                })
              ) : (
                <div className="p-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No urgent actions needed</p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-card border border-border rounded-md" data-testid="upcoming-deadlines-widget">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Upcoming Deadlines</h2>
              </div>
            </div>
            <div className="divide-y divide-border max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : stats?.upcoming_deadlines?.length > 0 ? (
                stats.upcoming_deadlines.slice(0, 8).map((dl, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dl.days_left <= 7 ? 'bg-rose-500' : dl.days_left <= 14 ? 'bg-amber-400' : 'bg-sky-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{dl.driver_name} — <span className="text-muted-foreground">{dl.detail}</span></p>
                    </div>
                    <span className={`text-xs font-medium flex-shrink-0 ${dl.days_left <= 7 ? 'text-rose-500' : 'text-amber-500'}`}>{dl.days_left}d</span>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <CalendarClock className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
                </div>
              )}
            </div>
          </div>

          {/* Pending Applications */}
          <div className="bg-card border border-border rounded-md" data-testid="pending-apps-widget">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-sky-500" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Pending Applications</h2>
              </div>
              <Link to="/driver-files">
                <Button variant="ghost" size="sm" className="h-7 text-xs">View All <ChevronRight className="h-3 w-3 ml-1" /></Button>
              </Link>
            </div>
            <div className="divide-y divide-border">
              {loading ? (
                <div className="p-4 space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : apps.length > 0 ? (
                apps.map((app, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-xs font-bold text-muted-foreground">
                      {app.first_name?.[0]}{app.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{app.first_name} {app.last_name}</p>
                      <p className="text-xs text-muted-foreground">{app.email || app.phone}</p>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No pending applications</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Compliance Snapshot */}
          <div className="bg-card border border-border rounded-md" data-testid="compliance-snapshot">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Compliance Snapshot</h2>
            </div>
            <div className="p-4">
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : totalForDonut > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="w-40 h-40 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={complianceDonut} cx="50%" cy="50%" innerRadius={45} outerRadius={65} dataKey="value" stroke="none">
                          {complianceDonut.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => v} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="font-cabinet text-2xl font-bold text-foreground">{stats?.total_drivers || 0}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-3">
                    {complianceDonut.map((d, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                        <span className="text-muted-foreground">{d.name}: {d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShieldCheck className="h-10 w-10 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No driver data yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-md" data-testid="quick-actions-widget">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Quick Actions</h2>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              {[
                { label: 'Add Driver', icon: UserPlus, href: '/drivers', color: 'text-blue-600 dark:text-blue-400' },
                { label: 'Invite Applicant', icon: Send, action: () => setShareOpen(true), color: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Upload Docs', icon: Upload, href: '/driver-files', color: 'text-amber-600 dark:text-amber-400' },
                { label: 'Driver Files', icon: FileText, href: '/driver-files', color: 'text-purple-600 dark:text-purple-400' },
                { label: 'Export File', icon: Download, href: '/driver-management', color: 'text-sky-600 dark:text-sky-400' },
                { label: 'Driver List', icon: ClipboardList, href: '/driver-management', color: 'text-muted-foreground' },
              ].map((action, i) => action.action ? (
                <button key={i} onClick={action.action}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors duration-200"
                  data-testid={`qa-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <action.icon className={`h-4 w-4 ${action.color}`} />{action.label}
                </button>
              ) : (
                <Link key={i} to={action.href}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors duration-200"
                  data-testid={`qa-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <action.icon className={`h-4 w-4 ${action.color}`} />{action.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-card border border-border rounded-md" data-testid="recent-activity-widget">
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent Activity</h2>
              </div>
            </div>
            <div className="divide-y divide-border max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : stats?.recent_activity?.length > 0 ? (
                stats.recent_activity.map((act, i) => (
                  <div key={i} className="px-4 py-2.5">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{act.driver_name}</span>
                      <span className="text-muted-foreground/50"> — </span>
                      <span className="text-muted-foreground">{act.detail}</span>
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {act.timestamp ? new Date(act.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </p>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <Activity className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                </div>
              )}
            </div>
          </div>

          {/* Driver Status Breakdown */}
          <div className="bg-card border border-border rounded-md" data-testid="status-breakdown">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Driver Status</h2>
            </div>
            <div className="p-4 space-y-2">
              {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-6 w-full" />)}</div>
              ) : stats?.status_breakdown && (
                Object.entries(stats.status_breakdown).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between">
                    <StatusBadge status={key} />
                    <span className="font-cabinet text-sm font-bold text-foreground">{val}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
